import type { PalmModule } from "../types/palm";

// Type declarations for experimental APIs
interface MediaStreamTrackProcessorConstructor {
  new (options: { track: MediaStreamTrack }): MediaStreamTrackProcessor;
}

interface MediaStreamTrackProcessor {
  readable: ReadableStream<VideoFrame>;
}

interface HTMLVideoElementWithCaptureStream extends HTMLVideoElement {
  captureStream?(): MediaStream;
}

type GlobalWithWebCodecs = typeof globalThis & {
  MediaStreamTrackProcessor?: MediaStreamTrackProcessorConstructor;
};

let canvas: HTMLCanvasElement | null = null;
let videoElement: HTMLVideoElement | null = null;
let context: CanvasRenderingContext2D | null = null;

// Cache WebCodecs support check result
let webCodecsSupported: boolean | null = null;
let webCodecsSupportChecked = false;

// Cache canvas dimensions to avoid frequent resizing
let cachedCanvasWidth = 0;
let cachedCanvasHeight = 0;

// Reusable buffer for WebCodecs to reduce memory allocations
let reusableRgbaBuffer: Uint8ClampedArray | null = null;
let reusableBufferWidth = 0;
let reusableBufferHeight = 0;

// Type for navigator with deviceMemory (experimental API)
interface NavigatorWithDeviceMemory extends Navigator {
  deviceMemory?: number;
}

// Cache low-end device detection result (device capabilities don't change)
let isLowEndDeviceCached: boolean | null = null;

// Detect low-end device based on hardware concurrency and memory
const isLowEndDevice = (): boolean => {
  if (isLowEndDeviceCached !== null) {
    return isLowEndDeviceCached;
  }

  // Check hardware concurrency (CPU cores)
  const cores = navigator.hardwareConcurrency || 4;
  // Check device memory (if available)
  const deviceMemory =
    (navigator as NavigatorWithDeviceMemory).deviceMemory || 4;
  // Low-end devices typically have <= 4 cores and <= 4GB RAM
  // Also check user agent for Android low-end indicators
  const userAgent = navigator.userAgent.toLowerCase();
  const isAndroid = userAgent.includes("android");
  const isLowEndUA =
    userAgent.includes("android 4") || userAgent.includes("android 5");

  isLowEndDeviceCached =
    cores <= 4 || deviceMemory <= 4 || (isAndroid && isLowEndUA);
  return isLowEndDeviceCached;
};

export let CANVAS_IMAGE_WIDTH = 1;
export let CANVAS_IMAGE_HEIGHT = 1;

const getImageDataNew = () => {
  if (!canvas || !videoElement || !context) {
    canvas = document.getElementById("canvas") as HTMLCanvasElement;
    videoElement = document.getElementById("localVideo") as HTMLVideoElement;
    if (!canvas || !videoElement) {
      return;
    }
    context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) {
      return;
    }
  }
  const originalWidth = videoElement.videoWidth;
  const originalHeight = videoElement.videoHeight;
  if (originalWidth === 0 || originalHeight === 0) {
    return;
  }

  // Calculate scaled dimensions
  const scaleFactor = 1;
  const scaledWidth = Math.floor(originalWidth * scaleFactor);
  const scaledHeight = Math.floor(originalHeight * scaleFactor);

  CANVAS_IMAGE_WIDTH = scaledWidth;
  CANVAS_IMAGE_HEIGHT = scaledHeight;

  // Only resize canvas if dimensions changed (expensive operation)
  if (
    cachedCanvasWidth !== scaledWidth ||
    cachedCanvasHeight !== scaledHeight
  ) {
    canvas.width = scaledWidth;
    canvas.height = scaledHeight;
    cachedCanvasWidth = scaledWidth;
    cachedCanvasHeight = scaledHeight;
  }

  // Use putImageData optimization: only clear if needed
  // For performance, skip clearRect if we're overwriting the entire canvas
  context.drawImage(videoElement, 0, 0, scaledWidth, scaledHeight);
  const imageData = context?.getImageData(0, 0, scaledWidth, scaledHeight).data;
  return {
    data: imageData,
    width: scaledWidth,
    height: scaledHeight,
  };
};

let reader: ReadableStreamDefaultReader<VideoFrame> | null = null;
let webCodecsFailed = false; // Track if WebCodecs has failed to avoid repeated attempts

// Check if experimental WebCodecs API is supported (cached result)
const isWebCodecsSupported = (): boolean => {
  if (webCodecsSupportChecked) {
    return webCodecsSupported === true;
  }

  const global = globalThis as GlobalWithWebCodecs;
  const MediaStreamTrackProcessorConstructor = global.MediaStreamTrackProcessor;
  webCodecsSupported =
    typeof HTMLVideoElement !== "undefined" &&
    typeof (HTMLVideoElement.prototype as HTMLVideoElementWithCaptureStream)
      .captureStream !== "undefined" &&
    typeof MediaStreamTrackProcessorConstructor !== "undefined";
  webCodecsSupportChecked = true;
  return webCodecsSupported === true;
};

const initReader = (): boolean => {
  if (!videoElement) {
    videoElement = document.getElementById("localVideo") as HTMLVideoElement;
  }
  if (!videoElement) {
    return false;
  }

  try {
    const global = globalThis as GlobalWithWebCodecs;
    const MediaStreamTrackProcessorConstructor =
      global.MediaStreamTrackProcessor;
    if (!MediaStreamTrackProcessorConstructor) {
      return false;
    }
    const videoWithCapture = videoElement as HTMLVideoElementWithCaptureStream;
    if (!videoWithCapture.captureStream) {
      return false;
    }
    const track = videoWithCapture.captureStream().getVideoTracks()[0];
    if (!track) {
      return false;
    }
    const processor = new MediaStreamTrackProcessorConstructor({ track });
    reader = processor.readable.getReader();
    webCodecsFailed = false; // Reset failure flag on successful initialization
    return true;
  } catch {
    webCodecsFailed = true;
    reader = null; // Reset reader on error
    return false;
  }
};

const getImageDataFromWebCodecs = async () => {
  // If WebCodecs has failed before, skip it
  if (webCodecsFailed) {
    return null;
  }

  // Initialize reader if needed
  if (!reader) {
    const initialized = initReader();
    if (!initialized) {
      webCodecsFailed = true;
      return null;
    }
    // After initialization, try to read immediately
    // Don't return null on first call
  }

  try {
    if (!reader) {
      return null;
    }
    const { value: frame, done } = await reader.read();
    if (done || !frame) {
      // Stream ended, reset reader
      reader = null;
      return null;
    }

    // Convert VideoFrame to RGBA
    const width = frame.displayWidth;
    const height = frame.displayHeight;
    CANVAS_IMAGE_WIDTH = width;
    CANVAS_IMAGE_HEIGHT = height;

    // Reuse buffer if size matches, otherwise allocate new one
    const bufferSize = width * height * 4;
    let rgba: Uint8ClampedArray;
    if (
      reusableRgbaBuffer &&
      reusableBufferWidth === width &&
      reusableBufferHeight === height
    ) {
      rgba = reusableRgbaBuffer;
    } else {
      rgba = new Uint8ClampedArray(bufferSize);
      reusableRgbaBuffer = rgba;
      reusableBufferWidth = width;
      reusableBufferHeight = height;
    }

    // Convert VideoFrame to RGBA
    await frame.copyTo(rgba, {
      format: "RGBA",
    });

    frame.close(); // Must release, otherwise memory will spike

    return {
      data: rgba,
      width,
      height,
    };
  } catch {
    // On error, reset reader and mark as failed
    webCodecsFailed = true;
    reader = null;
    return null;
  }
};

export const getFrame = async (Module: PalmModule) => {
  const frame = new Module.PalmFrame();
  frame.create();

  // Try WebCodecs API first if supported and not failed before
  let imageData = null;
  if (isWebCodecsSupported() && !webCodecsFailed) {
    imageData = await getImageDataFromWebCodecs();
  }

  // Fallback to canvas method if WebCodecs failed or not supported
  if (!imageData || !imageData.data) {
    imageData = getImageDataNew();
  }

  if (!imageData || !imageData.data) {
    frame.free();
    return;
  }
  const imageDataSize = imageData.data.length;
  // console.log(`imageDataSize: `, imageDataSize);
  const imageDataPtr = Module._malloc(imageDataSize);
  if (imageDataPtr === 0) {
    console.error("Memory allocation failed for imageData.");
    frame.free();
    return;
  }

  Module.HEAPU8.set(imageData.data, imageDataPtr);

  // not a image
  frame.addEncodedImage(
    imageDataPtr,
    imageData.width,
    imageData.height,
    0,
    3,
    Module.PalmType.PalmType_Print
  );

  Module._free(imageDataPtr);
  imageData = undefined;
  return frame;
};

// Cleanup function to reset video utils state when component unmounts
export const cleanupVideoUtils = () => {
  if (reader) {
    reader.cancel().catch(() => {
      // Ignore cancellation errors
    });
    reader = null;
  }
  webCodecsFailed = false;
  videoElement = null;
  canvas = null;
  context = null;
};
